import type { ShotResolved, SkillUsed } from '@ddt/protocol';
import { SkillKind } from '@ddt/protocol';

/** Tipo do número que sobe da cabeça de quem foi atingido ou curado. */
export type DamageTextKind = 'physical' | 'magic' | 'heal' | 'criticalPhysical' | 'criticalMagic';

export interface DamageTextStyle {
  /** Cor do número (0xRRGGBB). */
  fill: number;
  /** Cor do contorno: escolhida para o número aparecer sobre qualquer cenário. */
  stroke: number;
  /** Críticos são maiores e levam o selo "CRÍTICO". */
  big: boolean;
}

/**
 * Cores do dano na tela: ataque físico em laranja, dano mágico em rosa/roxo, cura em verde. Crítico físico em vermelho e crítico
 * mágico em roxo escuro (com contorno claro, senão some no cenário escuro).
 */
export const DAMAGE_TEXT_STYLES: Record<DamageTextKind, DamageTextStyle> = {
  physical: { fill: 0xff9a2e, stroke: 0x4a1d00, big: false },
  magic: { fill: 0xf06bff, stroke: 0x3a0a45, big: false },
  heal: { fill: 0x4cff7a, stroke: 0x06391a, big: false },
  criticalPhysical: { fill: 0xff3b3b, stroke: 0xffffff, big: true },
  criticalMagic: { fill: 0x6a1b9a, stroke: 0xf6dcff, big: true },
};

export interface DamageText {
  /** Quem recebe o número: o texto sobe da cabeça dele. */
  accountId: string;
  text: string;
  kind: DamageTextKind;
}

type DamageOf = ShotResolved['damages'][number];

/**
 * Números de um tiro, um por alvo atingido (inclusive aliado e o próprio atirador, que também sofrem dano). Mostra o total perdido
 * (dano + crítico); crítico no alvo troca a cor e o tamanho. Golpe sem dano não gera número.
 */
export function damageTexts(damages: readonly DamageOf[]): DamageText[] {
  const texts: DamageText[] = [];
  for (const d of damages) {
    const total = d.damage + d.critical;
    if (total <= 0) continue;
    const kind: DamageTextKind = d.critical > 0 ? (d.magic ? 'criticalMagic' : 'criticalPhysical') : d.magic ? 'magic' : 'physical';
    texts.push({ accountId: d.targetId, text: String(total), kind });
  }
  return texts;
}

/** Número verde da cura de uma habilidade; `null` para qualquer outra habilidade ou cura vazia. */
export function healText(skill: Pick<SkillUsed, 'kind' | 'targetId' | 'amount'>): DamageText | null {
  if (skill.kind !== SkillKind.HEAL) return null;
  const amount = Math.round(skill.amount);
  return amount > 0 ? { accountId: skill.targetId, text: `+${amount}`, kind: 'heal' } : null;
}
